import {Error, ValidationResult, Validator} from '@/type/validation/result';
import {半角カナ禁止, 半角英数のみ, 必須入力, 日付書式, ゼロを許可する必須入力, ゼロ以上} from '@/type/validation/validator';
import moment from 'moment';
import {入庫候補発注補検索結果レコード型} from '@/type/stock/receive/order';

export default class 発注指定入庫入力チェッカー {
  単項目チェック結果: Error[];
  相関チェック結果: Error[];

  constructor() {
    this.単項目チェック結果 = [];
    this.相関チェック結果 = [];
  }

  validateList(target: 入庫候補発注補検索結果レコード型[]) {
    const 単項目チェック = new ValidationResult(入庫確定Validator);
    単項目チェック.validateList(target, validate対象である);
    this.単項目チェック結果 = 単項目チェック.getErrors;
    const 入庫日エラー = 入庫日が発注日より過去(target);
    const 有効期限エラー = 有効期限が入庫日付より過去(target);
    this.相関チェック結果 = 入庫日エラー.concat(有効期限エラー);
  }

  hasError(name: string, index: number = 0): boolean {
    return this.単項目チェック結果.concat(this.相関チェック結果).some((error) => (error.name === name && error.index === index));
  }

  hasErrors(): boolean {
    return this.単項目チェック結果.length > 0 || this.相関チェック結果.length > 0;
  }

  errorMessages(): string[] {
    const errorMessages = this.単項目チェック結果.concat(this.相関チェック結果)
      .sort((a, b) => a.index - b.index)
      .map((value) => `${value.message}`);
    return Array.from(new Set(errorMessages));
  }
}


function 入庫数は5桁以内(value: number): boolean {
  return value <= 99999;
}

function マイナス不可(value: number): boolean {
  return value > -1;
}

function 未来日時は不可(value: Date): boolean {
  if (value === null) {
    return true;
  }
  if (日付書式(value) === false) {
    return true;
  }
  const momentValue = moment(value);
  const momentNow = moment(new Date());
  return momentValue.isBefore(momentNow);
}

function 入力単価は9桁以内(value: number): boolean {
  return value <= 999999999;
}

function ロット番号桁数検証(value: string): boolean {
  if (!value) {
    return true;
  }
  return value.length <= 20;
}

function 伝票番号桁数検証(value: string): boolean {
  if (!value) {
    return true;
  }
  return value.length <= 10;
}

function 伝票行番号は2桁以内(value: number): boolean {
  if (!value) {
    return true;
  }
  return value <= 99;
}

export const 入庫確定Validator: Validator[] = [
  {
    name: '入庫日付',
    constraints:  [
      {method: 必須入力, message: '入庫日付は入力必須項目です'},
      {method: 日付書式, message: '入庫日付の書式が不正です'},
      {method: 未来日時は不可, message: '入庫日付は未来の日付への変更はできません'},
    ],
  },
  {
    name: '入庫単価',
    constraints: [
      {method: ゼロを許可する必須入力, message: '単価は入力必須項目です'},
      {method: ゼロ以上, message: '単価は0以上で入力してください'},
      {method: 入力単価は9桁以内, message: '単価は整数部9桁以内で入力してください'},
    ],
  },
  {
    name: '入庫数',
    constraints: [
      {method: 必須入力, message: '入庫数は入力必須項目です'},
      {method: マイナス不可, message: '入庫数にマイナスの値は入力できません'},
      {method: 入庫数は5桁以内, message: '入庫数は整数部5桁以内で入力してください'},
    ],
  },
  {
    name: '有効期限',
    constraints: [
      {method: 必須入力, message: '有効期限は入力必須項目です'},
      {method: 日付書式, message: '有効期限の書式が不正です'},
    ],
  },
  {
    name: 'ロット番号',
    constraints: [
      {method: ロット番号桁数検証, message: 'ロット番号は20文字以下で入力してください'},
    ],
  },
  {
    name: '卸伝票番号',
    constraints: [
      {method: 伝票番号桁数検証, message: '卸伝票番号は10文字以下で入力してください'},
      {method: 半角英数のみ, message: '卸伝票番号は半角英数字で入力してください'},
    ],
  },
  {
    name: '卸伝票行番号',
    constraints: [
      {method: マイナス不可, message: '卸伝票行番号にマイナスの値は入力できません'},
      {method: 伝票行番号は2桁以内, message: '卸伝票行番号は整数部2桁以内で入力してください'},
    ],
  },
];

function 入庫日が発注日より過去(target: 入庫候補発注補検索結果レコード型[]): Error[] {
  const errors: Error[] = [];
  target.forEach((value, index): void => {
    if (validate対象である(value) && moment(value.入庫日付) < moment(value.発注日)) {
      errors.push({index, message: '入庫日付に発注日以前の日付は指定できません', constraint: '', name: '入庫日付'});
    }
  });
  return errors;
}

function 有効期限が入庫日付より過去(target: 入庫候補発注補検索結果レコード型[]): Error[] {
  const errors: Error[] = [];
  target.forEach((value, index): void => {
    if (validate対象である(value) && moment(value.有効期限) < moment(value.入庫日付)) {
      errors.push({index, message: '有効期限が入庫日付より前の日付です', constraint: '', name: '有効期限'});
    }
  });
  return errors;
}

function validate対象である(target: 入庫候補発注補検索結果レコード型) {
  return target.選択 === true;
}
